import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import ClimateHazardIcon from '../common/ClimateHazardIcon'
import ClimateContentMotion from '../common/ClimateContentMotion'
import { getClimateHazardViewConfig, getClimateHazardVisualMeta } from '../common/hazardConfig'
import { DEFAULT_SCORE_PALETTE } from '../common/ScoreBadge'
import PortfolioInfoPopup from './PortfolioInfoPopup'
import { RISK_BANDS, supportedHorizonLabel, type PortfolioHazardRisk } from './portfolioRisk'
import type { ReactNode } from 'react'

type Props = {
  risk: PortfolioHazardRisk
  transitionKey: string
  onOpen: () => void
  settings?: ReactNode
}

export function HazardRiskMetadata({ risk }: { risk: PortfolioHazardRisk }) {
  const { summary } = risk
  const supportsAdaptations = Boolean(getClimateHazardViewConfig(risk.hazard.name).supportsAdaptations)
  const scenarios = Array.from(
    new Set(
      Object.values(risk.byProperty)
        .map(value => value.scenario)
        .filter(Boolean),
    ),
  )
  const resultStatus = !risk.config
    ? 'Rating not configured'
    : !risk.periodAvailable
    ? 'Horizon unavailable'
    : risk.error
    ? 'Load failed'
    : risk.loading
    ? 'Loading…'
    : null
  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        The average is an unweighted mean of available property risk ratings on a 1–5 scale. High-risk properties are
        those in bands 4 or 5{risk.hazard.name === 'wr' ? ' (High or Extremely High)' : ''}. Ratings remain
        hazard-specific, not a combined climate score.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        The distribution includes every property in the portfolio. Grey represents properties without a rating for this
        selection and is excluded from the average and high-risk count.
      </Typography>
      {risk.hazard.name === 'hs' && (
        <Typography variant="body2" color="text.secondary">
          Each property's heat index is converted to a risk band before calculating the average.
        </Typography>
      )}
      {risk.hazard.name === 'wr' && (
        <Typography variant="body2" color="text.secondary">
          Aqueduct's continuous 0–5 score is mapped to five risk bands: 0–1 is Low, 1–2 Low-Medium, 2–3 Medium-High, 3–4
          High, and 4–5 Extremely High.
        </Typography>
      )}
      <Divider />
      <Typography variant="body2">
        Score contributors: <strong>{resultStatus ?? `${summary.contributors} of ${summary.total} properties`}</strong>
      </Typography>
      <Typography variant="body2">
        High / Very High: <strong>{resultStatus ? '—' : summary.highCount ?? '—'}</strong>
      </Typography>
      <Typography variant="body2">
        Supported horizons: <strong>{supportedHorizonLabel(risk.hazard.name)}</strong>
      </Typography>
      <Typography variant="body2">
        Scenario source: <strong>{resultStatus ?? (scenarios.join(', ') || 'No contributing results')}</strong>
      </Typography>
      <Divider />
      <Typography variant="body2">
        Adaptations:{' '}
        <strong>
          {!supportsAdaptations
            ? 'Not available for this hazard'
            : risk.adaptationsEnabled
            ? 'Enabled where available'
            : 'Off (raw results)'}
        </strong>
      </Typography>
      <Typography variant="body2">
        Adapted contributors:{' '}
        <strong>{resultStatus || summary.unknownContributors > 0 ? '—' : summary.adaptedContributors}</strong>
      </Typography>
      <Typography variant="body2">
        Raw contributors:{' '}
        <strong>{resultStatus || summary.unknownContributors > 0 ? '—' : summary.baselineContributors}</strong>
      </Typography>
      {risk.error && (
        <Button variant="outlined" size="small" onClick={risk.retry}>
          Retry loading results
        </Button>
      )}
    </Stack>
  )
}

function RiskDistribution({ risk }: { risk: PortfolioHazardRisk }) {
  const { summary } = risk
  const withoutData = Math.max(0, summary.total - summary.contributors)
  return (
    <Box sx={{ mt: 2.25 }}>
      {risk.loading ? (
        <Skeleton variant="rounded" height={9} />
      ) : (
        <Stack
          direction="row"
          spacing={0.25}
          sx={{ height: 9, borderRadius: 2, overflow: 'hidden' }}
          aria-label={`${risk.hazard.display_name} risk distribution`}
        >
          {RISK_BANDS.filter(band => summary.distribution[band] > 0).map(band => (
            <Tooltip key={band} title={`${risk.config?.labels[band]}: ${summary.distribution[band]} properties`} arrow>
              <Box
                role="img"
                aria-label={`${risk.config?.labels[band]}: ${summary.distribution[band]} properties`}
                data-risk-band={band}
                sx={{
                  flex: summary.distribution[band],
                  backgroundColor: (DEFAULT_SCORE_PALETTE[band] as { background: string }).background,
                }}
              />
            </Tooltip>
          ))}
          {withoutData > 0 && (
            <Tooltip title={`No data: ${withoutData} properties`} arrow>
              <Box
                role="img"
                aria-label={`No data: ${withoutData} properties`}
                data-risk-band="no-data"
                sx={{ flex: withoutData, backgroundColor: 'grey.300' }}
              />
            </Tooltip>
          )}
        </Stack>
      )}
      {!risk.loading && withoutData > 0 && (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" mt={0.75}>
          <Box sx={{ width: 7, height: 7, bgcolor: 'grey.300', borderRadius: '50%' }} />
          <Typography variant="caption" color="text.secondary">
            No data
          </Typography>
        </Stack>
      )}
    </Box>
  )
}

export default function PortfolioHazardCard({ risk, transitionKey, onOpen, settings }: Props) {
  const meta = getClimateHazardVisualMeta(risk.hazard.name)
  const { summary } = risk
  const unavailable = !risk.config
    ? 'Overview rating not configured'
    : !risk.periodAvailable
    ? `Available for ${supportedHorizonLabel(risk.hazard.name)} only`
    : risk.error
    ? 'Unable to load risk results'
    : !risk.loading && summary.average == null
    ? 'No risk results for this selection'
    : null

  return (
    <Card
      variant="outlined"
      sx={{
        p: 0,
        height: '100%',
        position: 'relative',
        borderRadius: 3,
        borderColor: 'divider',
        transition: 'box-shadow 180ms ease, border-color 180ms ease',
        '&:hover': { boxShadow: 4, borderColor: meta.tone },
      }}
    >
      <Stack direction="row" sx={{ position: 'absolute', top: 16, right: 12, zIndex: 1 }}>
        {settings}
        <PortfolioInfoPopup title={`${risk.hazard.display_name} risk`}>
          <HazardRiskMetadata risk={risk} />
        </PortfolioInfoPopup>
      </Stack>
      <CardActionArea onClick={onOpen} aria-label={`Explore ${risk.hazard.display_name}`} sx={{ height: '100%' }}>
        <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ pr: settings ? 8 : 4, minHeight: 40, mb: 2.5 }}
          >
            <ClimateHazardIcon hazardCode={risk.hazard.name} size={38} iconSize={22} borderRadius={2} />
            <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.25 }}>
              {risk.hazard.display_name}
            </Typography>
          </Stack>
          <ClimateContentMotion transitionKey={transitionKey} minHeight={132}>
            {unavailable ? (
              <Stack justifyContent="center" sx={{ minHeight: 132 }}>
                <Typography variant="body2" color={risk.error ? 'error.main' : 'text.secondary'}>
                  {unavailable}
                </Typography>
                {risk.config && risk.periodAvailable && !risk.error && summary.total > 0 && (
                  <RiskDistribution risk={risk} />
                )}
              </Stack>
            ) : (
              <>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Average risk score
                    </Typography>
                    {risk.loading ? (
                      <Skeleton width={100} height={50} />
                    ) : (
                      <Typography variant="h4" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums', mt: 0.25 }}>
                        {summary.average?.toFixed(1)}
                        <Typography component="span" variant="body2" color="text.secondary">
                          {' '}
                          / 5
                        </Typography>
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">
                      High / Very High
                    </Typography>
                    {risk.loading ? (
                      <Skeleton width={75} height={50} />
                    ) : (
                      <Typography
                        variant="h4"
                        fontWeight={800}
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          mt: 0.25,
                          color: summary.highCount ? 'error.dark' : 'text.primary',
                        }}
                      >
                        {summary.highCount}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      properties
                    </Typography>
                  </Box>
                </Stack>
                <RiskDistribution risk={risk} />
              </>
            )}
          </ClimateContentMotion>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              mt: 'auto',
              pt: 2,
              color: meta.tone,
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              Explore hazard
            </Typography>
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: alpha(meta.tone, 0.08),
              }}
            >
              <ArrowForwardRoundedIcon sx={{ fontSize: 17 }} />
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}
